/**
 * Administração › Empresas: lista, nova empresa e detalhe (dados, situação e histórico) — Optimale Grid (frontend, US-1901).
 *
 * Criado em: 19/10/2026 · Última modificação: 02/11/2026 · Versão: 2.0.0
 *
 * ADR-0014: cadastro estrutural, só a equipe Optimale cria e altera; o suporte consulta as operadoras atribuídas.
 * Cadastrar cria só o registro (situação "rascunho"); a implantação é outra etapa (Sprint 22).
 * v2.0 (US-4604, Sprint 46): lista com migalhas, KPIs que filtram (total, ativas, em implantação, rascunho, suspensas),
 * busca com `/` e filtros no endereço com pílulas, tabela densa com painel de detalhe ao lado; formulário em grupos com
 * validação ao sair do campo e rodapé fixo; detalhe com o histórico em linha do tempo e motivo de suspender/encerrar num
 * diálogo próprio. Mesmas chamadas e permissões de antes.
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ErroApi } from "../api";
import { NOMES } from "../nomes";
import { pode } from "../permissoes";
import { useSessao } from "../sessao";
import { AbasAdmin } from "./AbasAdmin";
import { Filiais } from "./Filiais";
import { Municipios } from "./Municipios";
import { Setores } from "./Setores";
import { quando } from "./tipos";
import { marcaAtual } from "../marca";
import {
  avisar, BarraFiltros, CabecalhoTela, Campo, DialogoMotivo, EstadoVazio, ErroCarregar, Esqueleto, FaixaKpis, GrupoCampos, ListaDetalhe,
  RodapeFormulario, Segmentado, Selo, TabelaDensa, useAvisoAoSair, useLembrarEndereco, useParametro, type Coluna,
} from "../componentes/Tela";
import { idadeRelativa, ordenar, type Ordem } from "../componentes/regrasTela";
import "./estrutura.css";

export type Empresa = {
  id: string; razao_social: string; nome_fantasia: string; cnpj: string | null; uf: string; fuso: string;
  contato_nome: string; contato_email: string; contato_telefone: string; simulacao: boolean;
  situacao: "rascunho" | "em_implantacao" | "ativa" | "suspensa" | "encerrada"; motivo_situacao: string;
  versao: number; criado_por: string; criado_em: string; atualizado_em: string;
};
type EventoEmpresa = { instante: string; autor: string; evento: string; detalhe: Record<string, unknown> };

export const SITUACAO_EMPRESA: Record<Empresa["situacao"], [string, string]> = {
  rascunho: ["Rascunho", "situacao-pendente"], em_implantacao: ["Em implantação", "situacao-pendente"],
  ativa: ["Ativa", "situacao-validado"], suspensa: ["Suspensa", "situacao-rejeitado"], encerrada: ["Encerrada", "situacao-rejeitado"],
};
const TOM_SITUACAO: Record<Empresa["situacao"], "ok" | "atencao" | "ruim" | "suave"> = {
  rascunho: "suave", em_implantacao: "atencao", ativa: "ok", suspensa: "ruim", encerrada: "ruim" };
const PROXIMAS: Record<Empresa["situacao"], Empresa["situacao"][]> = {
  rascunho: ["em_implantacao", "encerrada"], em_implantacao: ["rascunho", "ativa", "encerrada"],
  ativa: ["suspensa", "encerrada"], suspensa: ["ativa", "encerrada"], encerrada: [],
};
const EVENTO: Record<string, string> = { criada: "Cadastrada", alterada: "Dados alterados", situacao_alterada: "Situação alterada",
  registrada_existente: "Registrada a partir do hub existente", filial_criada: "Filial cadastrada", filial_alterada: "Filial renomeada",
  filial_inativada: "Filial inativada", filial_reativada: "Filial reativada", filial_excluida: "Filial excluída",
  implantacao_passo: "Passo da implantação", implantacao_concluida: "Implantação concluída",
  municipio_criado: "Município cadastrado", municipio_alterado: "Município alterado", municipio_excluido: "Município excluído",
  setor_criado: "Setor cadastrado", setor_alterado: "Setor alterado", setor_inativado: "Setor inativado", setor_reativado: "Setor reativado",
  setor_excluido: "Setor excluído" };
const nomeSituacao = (s: unknown) => SITUACAO_EMPRESA[s as Empresa["situacao"]]?.[0] ?? String(s);
const formatarCnpj = (c: string | null) => (c ? c.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5") : "—");
const nomeEmpresa = (e: Empresa) => e.nome_fantasia || e.razao_social;
const MIGALHA_ADMIN = { texto: NOMES.administracao };
const SEM_ACESSO = () => <p className="nx-suave">Cadastro estrutural: só a equipe {marcaAtual().empresa}.</p>;

/** Ficha curta da empresa (painel de detalhe da lista e cartão do detalhe). */
function FichaEmpresa({ e }: { e: Empresa }) {
  return (
    <dl className="tl-kv">
      <dt>Identificador</dt><dd>{e.id}</dd>
      <dt>Razão social</dt><dd>{e.razao_social}</dd>
      <dt>CNPJ</dt><dd>{formatarCnpj(e.cnpj)}</dd>
      <dt>UF · fuso</dt><dd>{e.uf || "—"} · {e.fuso}</dd>
      <dt>Contato</dt><dd>{[e.contato_nome, e.contato_email, e.contato_telefone].filter(Boolean).join(" · ") || "—"}</dd>
      <dt>Simulação</dt><dd>{e.simulacao ? "sim (testes e demonstração)" : "não"}</dd>
      {e.motivo_situacao && <><dt>Motivo da situação</dt><dd>{e.motivo_situacao}</dd></>}
      <dt>Cadastrada</dt><dd>{quando(e.criado_em)} por {e.criado_por}</dd>
      <dt>Atualizada</dt><dd>{idadeRelativa(e.atualizado_em)} · versão {e.versao}</dd>
    </dl>
  );
}

export function Empresas() {
  const { usuario } = useSessao();
  const navegar = useNavigate();
  useLembrarEndereco();
  const [busca, setBusca] = useParametro("busca");
  const [kpi, setKpi] = useParametro("situacao");
  const [operacao, setOperacao] = useParametro("operacao");
  const [sel, setSel] = useParametro("sel");
  const [ordem, setOrdem] = useState<Ordem>({ coluna: "nome", crescente: true });
  const [painelAberto, setPainelAberto] = useState(false);
  const [lista, setLista] = useState<Empresa[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [tentativa, setTentativa] = useState(0);
  useEffect(() => {
    const espera = window.setTimeout(() => {
      setErro(null);
      api<Empresa[]>(`/v1/admin/empresas${busca ? `?busca=${encodeURIComponent(busca)}` : ""}`).then(setLista)
        .catch((e) => setErro(String(e.message ?? e)));
    }, 250);
    return () => window.clearTimeout(espera);
  }, [busca, tentativa]);
  const contagem = useMemo(() => {
    const c = { total: 0, ativa: 0, em_implantacao: 0, rascunho: 0, suspensa: 0, encerrada: 0, simulacao: 0 };
    for (const e of lista ?? []) { c.total++; c[e.situacao]++; if (e.simulacao) c.simulacao++; }
    return c;
  }, [lista]);
  const visiveis = useMemo(() => {
    const filtradas = (lista ?? []).filter((e) => (!kpi || e.situacao === kpi)
      && (!operacao || (operacao === "simulacao" ? e.simulacao : !e.simulacao)));
    const valor = (e: Empresa) => ordem.coluna === "id" ? e.id : ordem.coluna === "uf" ? e.uf : ordem.coluna === "situacao" ? nomeSituacao(e.situacao)
      : ordem.coluna === "atualizada" ? e.atualizado_em : nomeEmpresa(e).toLowerCase();
    return ordenar(filtradas, valor, ordem.crescente);
  }, [lista, kpi, operacao, ordem]);
  if (!usuario || !pode.verEstrutura(usuario)) return <SEM_ACESSO />;
  const selecionada = visiveis.find((e) => e.id === sel) ?? visiveis[0] ?? null;
  const edita = pode.editarEstrutura(usuario);
  const pilulas = [
    ...(kpi ? [{ chave: "situacao", texto: nomeSituacao(kpi) }] : []),
    ...(operacao ? [{ chave: "operacao", texto: operacao === "simulacao" ? "Só simulação" : "Só operação real" }] : []),
    ...(busca ? [{ chave: "busca", texto: `“${busca}”` }] : []),
  ];
  const tirar = (chave: string) => ({ situacao: setKpi, operacao: setOperacao, busca: setBusca } as Record<string, (v: string) => void>)[chave]?.("");
  const limpar = () => { setKpi(""); setOperacao(""); setBusca(""); };
  const colunas: Coluna<Empresa>[] = [
    { chave: "nome", titulo: "Empresa", ordenavel: true, celula: (e) => (
      <div className="tl-ident"><div style={{ minWidth: 0 }}><b>{nomeEmpresa(e)}</b>
        <span>{e.nome_fantasia ? e.razao_social : "—"}{e.simulacao ? " · simulação" : ""}</span></div></div>) },
    { chave: "id", titulo: "Identificador", ordenavel: true, celula: (e) => <span className="tl-mono">{e.id}</span> },
    { chave: "cnpj", titulo: "CNPJ", celula: (e) => <span className="tl-mono">{formatarCnpj(e.cnpj)}</span> },
    { chave: "uf", titulo: "UF", ordenavel: true, celula: (e) => e.uf || "—" },
    { chave: "situacao", titulo: "Situação", ordenavel: true, celula: (e) => <Selo tom={TOM_SITUACAO[e.situacao]}>{SITUACAO_EMPRESA[e.situacao][0]}</Selo> },
    { chave: "atualizada", titulo: "Atualizada", ordenavel: true, celula: (e) => <span title={quando(e.atualizado_em)}>{idadeRelativa(e.atualizado_em)}</span> },
  ];
  return (
    <section className="tl-pagina admin-estrutura">
      <AbasAdmin usuario={usuario} />
      <CabecalhoTela migalhas={[MIGALHA_ADMIN, { texto: NOMES.empresas }]} titulo={NOMES.empresas}
        subtitulo="operadoras de saneamento atendidas"
        acoes={edita ? <Link className="nx-botao nx-botao-principal" to="/admin/empresas/nova">Nova empresa</Link> : undefined} />
      {erro ? <ErroCarregar mensagem={`Não foi possível carregar as empresas: ${erro}`} aoTentar={() => setTentativa((t) => t + 1)} /> : !lista ? <Esqueleto linhas={6} /> : (
        <>
          <FaixaKpis ativo={kpi} aoEscolher={setKpi} itens={[
            { chave: "", rotulo: "Empresas", valor: contagem.total, detalhe: `${contagem.simulacao} de simulação` },
            { chave: "ativa", rotulo: "Ativas", valor: contagem.ativa, detalhe: "em operação" },
            { chave: "em_implantacao", rotulo: "Em implantação", valor: contagem.em_implantacao, detalhe: "serviços sendo montados" },
            { chave: "rascunho", rotulo: "Rascunho", valor: contagem.rascunho, detalhe: "só o registro" },
            { chave: "suspensa", rotulo: "Suspensas", valor: contagem.suspensa, detalhe: `${contagem.encerrada} encerradas`, tom: contagem.suspensa ? "alarme" : undefined },
          ]} />
          <BarraFiltros busca={busca} aoBuscar={setBusca} placeholder="Buscar por identificador, razão social ou CNPJ" pilulas={pilulas} aoTirar={tirar} aoLimpar={limpar}>
            <Segmentado rotulo="Tipo de operação" valor={operacao} aoMudar={setOperacao}
              opcoes={[{ valor: "", texto: "Todas" }, { valor: "real", texto: "Operação real" }, { valor: "simulacao", texto: "Simulação" }]} />
          </BarraFiltros>
          <ListaDetalhe aberto={painelAberto}
            lista={(
              <TabelaDensa rotulo={NOMES.empresas} colunas={colunas} linhas={visiveis} chave={(e) => e.id} selecionada={selecionada?.id ?? null}
                aoSelecionar={(e) => { setSel(e.id); setPainelAberto(true); }} aoAbrir={(e) => navegar(`/admin/empresas/${e.id}`)}
                ordem={ordem} aoOrdenar={setOrdem}
                cabecalho={<span><b>{visiveis.length}</b> de {lista.length} empresas</span>}
                vazio={<EstadoVazio titulo="Nenhuma empresa com esses filtros" texto="Tire um filtro ou cadastre a operadora que falta."
                  acao={pilulas.length ? <button type="button" className="nx-botao" onClick={limpar}>Limpar filtros</button>
                    : edita ? <Link className="nx-botao" to="/admin/empresas/nova">Cadastrar empresa</Link> : undefined} />}
                cartao={(e) => (
                  <>
                    <div className="tl-linha-acoes" style={{ justifyContent: "space-between" }}>
                      <b>{nomeEmpresa(e)}</b><Selo tom={TOM_SITUACAO[e.situacao]}>{SITUACAO_EMPRESA[e.situacao][0]}</Selo></div>
                    <span className="tl-mono nx-suave">{e.id} · {e.uf || "—"} · {formatarCnpj(e.cnpj)}</span>
                  </>
                )} />
            )}
            detalhe={selecionada ? (
              <div className="tl-det-corpo">
                <div className="tl-det-titulo"><div style={{ flex: 1, minWidth: 0 }}><h2>{nomeEmpresa(selecionada)}</h2><p>{selecionada.razao_social}</p></div>
                  <button type="button" className="tl-fantasma tl-fechar" onClick={() => setPainelAberto(false)} aria-label="Fechar o detalhe">×</button></div>
                <div className="tl-linha-acoes"><Selo tom={TOM_SITUACAO[selecionada.situacao]}>{SITUACAO_EMPRESA[selecionada.situacao][0]}</Selo>
                  {selecionada.simulacao && <Selo tom="suave">simulação</Selo>}</div>
                <FichaEmpresa e={selecionada} />
                <div className="tl-linha-acoes">
                  <Link className="nx-botao nx-botao-principal" to={`/admin/empresas/${selecionada.id}`}>Abrir empresa</Link>
                  <Link className="nx-botao" to={`/admin/empresas/${selecionada.id}/implantacao`}>Implantação</Link>
                  <Link className="nx-botao" to={`/admin/dispositivos?empresa=${encodeURIComponent(selecionada.id)}`}>Dispositivos</Link>
                </div>
              </div>
            ) : undefined} />
        </>
      )}
    </section>
  );
}

type Form = Omit<Empresa, "situacao" | "motivo_situacao" | "versao" | "criado_por" | "criado_em" | "atualizado_em">;
const VAZIA: Form = { id: "", razao_social: "", nome_fantasia: "", cnpj: "", uf: "", fuso: "America/Sao_Paulo", contato_nome: "",
  contato_email: "", contato_telefone: "", simulacao: false };
type Erros = Partial<Record<keyof Form, string>>;

/** Mesmas regras dos atributos de antes (pattern, required, minLength, type=email); o resto a API confere. */
function validar(f: Form, nova: boolean): Erros {
  const e: Erros = {};
  if (nova && !/^[a-z0-9][a-z0-9_]{0,49}$/.test(f.id)) e.id = "Letras minúsculas, números e _ (ex.: empresa03). Não muda depois.";
  if (f.razao_social.trim().length < 2) e.razao_social = "Escreva a razão social.";
  if (f.uf && !/^[A-Z]{2}$/.test(f.uf.toUpperCase())) e.uf = "Duas letras (ex.: MS).";
  if (f.contato_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.contato_email)) e.contato_email = "E-mail incompleto (ex.: nome@operadora.com.br).";
  return e;
}

function FormEmpresa({ inicial, nova, enviando, aoEnviar, aoCancelar }: {
  inicial: Form; nova: boolean; enviando: boolean; aoEnviar: (f: Form) => void; aoCancelar: () => void;
}) {
  const [f, setF] = useState<Form>(inicial);
  const [tocados, setTocados] = useState<Set<keyof Form>>(new Set());
  const muda = <K extends keyof Form>(k: K, v: Form[K]) => setF((a) => ({ ...a, [k]: v }));
  const erros = validar(f, nova);
  const valido = Object.keys(erros).length === 0;
  const sujo = (Object.keys(f) as (keyof Form)[]).some((k) => f[k] !== inicial[k]);
  useAvisoAoSair(sujo && !enviando);
  const enviar = (e?: FormEvent) => { e?.preventDefault(); if (valido && sujo) aoEnviar(f); else setTocados(new Set(Object.keys(f) as (keyof Form)[])); };
  const campo = (k: keyof Form, rotulo: string, extra: Record<string, unknown> = {}, ajuda?: string, largo?: boolean) => {
    const erro = tocados.has(k) ? erros[k] : undefined;
    return (
      <Campo id={`emp-${k}`} rotulo={rotulo} erro={erro} ajuda={ajuda} largo={largo}>
        <input id={`emp-${k}`} value={String(f[k] ?? "")} aria-invalid={!!erro} aria-describedby={erro ? `emp-${k}-erro` : undefined}
          onBlur={() => setTocados((t) => new Set(t).add(k))} onChange={(e) => muda(k, e.target.value as never)} {...extra} />
      </Campo>
    );
  };
  return (
    <form className="tl-bloco" onSubmit={enviar} aria-label={nova ? "Nova empresa" : "Dados da empresa"}>
      <div className="tl-painel-aba">
        <GrupoCampos titulo="Identificação">
          {nova && campo("id", "Identificador", { required: true, placeholder: "ex.: empresa03 (não muda depois)" }, "É o código da operadora no tópico MQTT e no banco")}
          {campo("razao_social", "Razão social", { required: true }, undefined, !nova)}
          {campo("nome_fantasia", "Nome fantasia", {}, "É o nome que aparece nas telas")}
          {campo("cnpj", "CNPJ", { placeholder: "00.000.000/0000-00" })}
        </GrupoCampos>
        <GrupoCampos titulo="Localização">
          {campo("uf", "UF", { maxLength: 2 })}
          {campo("fuso", "Fuso horário", {}, "ex.: America/Sao_Paulo")}
        </GrupoCampos>
        <GrupoCampos titulo="Contato responsável">
          {campo("contato_nome", "Contato responsável")}
          {campo("contato_email", "E-mail do contato", { type: "email" })}
          {campo("contato_telefone", "Telefone do contato")}
        </GrupoCampos>
        <GrupoCampos titulo="Operação">
          <label className="opcao opcao-larga">
            <input type="checkbox" checked={f.simulacao} onChange={(e) => muda("simulacao", e.target.checked)} />
            Operadora de simulação (testes e demonstração)</label>
        </GrupoCampos>
      </div>
      <RodapeFormulario sujo={sujo} valido={valido} salvando={enviando} aoCancelar={aoCancelar} aoSalvar={() => enviar()}
        rotuloSalvar={nova ? "Cadastrar empresa" : "Salvar alterações"} />
    </form>
  );
}

const EDITAVEIS = ["razao_social", "nome_fantasia", "cnpj", "uf", "fuso", "contato_nome", "contato_email", "contato_telefone",
  "simulacao"] as const;
/** Só os campos que a API aceita (a tela guarda também situação, versão e datas). */
const corpo = (f: Form, comId: boolean) => ({
  ...Object.fromEntries(EDITAVEIS.map((k) => [k, f[k]])), ...(comId ? { id: f.id } : {}), cnpj: f.cnpj || null, uf: f.uf.toUpperCase(),
});

export function EmpresaNova() {
  const { usuario } = useSessao();
  const navegar = useNavigate();
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  if (!usuario || !pode.editarEstrutura(usuario)) return <SEM_ACESSO />;
  const criar = async (f: Form) => {
    setEnviando(true); setErro(null);
    try { const e = await api<Empresa>("/v1/admin/empresas", { corpo: corpo(f, true) }); avisar(`Empresa ${e.id} cadastrada (rascunho).`); navegar(`/admin/empresas/${e.id}`); }
    catch (e) { setErro(e instanceof ErroApi ? (e.status === 409 ? "Identificador ou CNPJ já cadastrado." : e.message) : "Falha de rede."); setEnviando(false); }
  };
  return (
    <section className="tl-pagina admin-estrutura">
      <CabecalhoTela voltar="/admin/empresas" migalhas={[MIGALHA_ADMIN, { texto: NOMES.empresas, para: "/admin/empresas" }, { texto: "Nova empresa" }]}
        titulo="Nova empresa" subtitulo="só o registro, em rascunho" />
      <p className="nx-suave" style={{ margin: 0 }}>Cadastrar cria só o registro (situação "Rascunho"). A implantação — banco, painéis, serviços e o primeiro administrador — é outra etapa.</p>
      {erro && <ErroCarregar mensagem={erro} />}
      <FormEmpresa inicial={VAZIA} nova enviando={enviando} aoEnviar={(f) => void criar(f)} aoCancelar={() => navegar("/admin/empresas")} />
    </section>
  );
}

/** Texto do evento do histórico, o mesmo de antes (quem, o quê e o detalhe). */
function detalheEvento(x: EventoEmpresa): string {
  return `${x.evento.startsWith("filial_") ? ` (${String(x.detalhe.filial)})` : ""}`
    + `${typeof x.detalhe.setor === "string" ? ` (${x.detalhe.setor})` : typeof x.detalhe.municipio === "string" ? ` (${x.detalhe.municipio}${x.evento === "municipio_alterado" ? `: ${Object.keys(x.detalhe).filter((k) => k !== "municipio").join(", ")}` : ""})` : ""}`
    + `${x.evento === "situacao_alterada" ? ` (${nomeSituacao(x.detalhe.de)} → ${nomeSituacao(x.detalhe.para)})` : x.evento === "alterada" ? ` (${Object.keys((x.detalhe.mudancas ?? {}) as object).join(", ")})` : ""}`;
}

export function EmpresaDetalhe() {
  const { id } = useParams();
  const { usuario } = useSessao();
  const [e, setE] = useState<Empresa | null>(null);
  const [eventos, setEventos] = useState<EventoEmpresa[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [editando, setEditando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [nova, setNova] = useState<Empresa["situacao"] | "">("");
  const [pedirMotivo, setPedirMotivo] = useState(false);
  const carregar = useCallback(() => {
    if (!id) return;
    setErro(null);
    api<Empresa>(`/v1/admin/empresas/${id}`).then(setE).catch((x) => setErro(x instanceof ErroApi && x.status === 404 ? "Empresa não encontrada." : String(x.message ?? x)));
    api<EventoEmpresa[]>(`/v1/admin/empresas/${id}/auditoria?limite=50`).then(setEventos).catch(() => setEventos([]));
  }, [id]);
  useEffect(carregar, [carregar]);
  const migalhas = [MIGALHA_ADMIN, { texto: NOMES.empresas, para: "/admin/empresas" }, { texto: e && e.id === id ? nomeEmpresa(e) : id ?? "" }];
  if (!usuario) return null;
  if (erro) return <section className="tl-pagina admin-estrutura"><CabecalhoTela voltar="/admin/empresas" migalhas={migalhas} titulo="Empresa" />
    <ErroCarregar mensagem={erro} aoTentar={carregar} /></section>;
  if (!e || e.id !== id) return <section className="tl-pagina admin-estrutura"><Esqueleto linhas={10} /></section>;
  if (!pode.verEmpresa(usuario, e.id)) return <p className="nx-suave">Empresa não encontrada.</p>;
  const edita = pode.editarEstrutura(usuario);
  const salvar = async (f: Form) => {
    setEnviando(true);
    try { setE(await api<Empresa>(`/v1/admin/empresas/${e.id}`, { metodo: "PUT", corpo: { versao: e.versao, ...corpo(f, false) } })); setEditando(false); setAviso(null); avisar("Alterações salvas."); carregar(); }
    catch (x) {
      if (x instanceof ErroApi && x.status === 409 && /versão/.test(x.message)) { setAviso("Outra pessoa alterou esta empresa antes. Os dados foram recarregados."); setEditando(false); carregar(); }
      else setAviso(x instanceof ErroApi ? `Não foi possível salvar: ${x.message}` : "Falha de rede.");
    } finally { setEnviando(false); }
  };
  const mudarSituacao = async (motivo: string) => {
    if (!nova) return;
    try { await api(`/v1/admin/empresas/${e.id}/situacao`, { corpo: { situacao: nova, motivo } }); setAviso(null); avisar(`Situação: ${SITUACAO_EMPRESA[nova][0]}.`); setNova(""); carregar(); }
    catch (x) { setAviso(x instanceof ErroApi ? `Não foi possível: ${x.message}` : "Falha de rede."); }
    finally { setPedirMotivo(false); }
  };
  const aplicar = () => { if (nova === "suspensa" || nova === "encerrada") setPedirMotivo(true); else void mudarSituacao(""); };
  const [rotulo, classe] = SITUACAO_EMPRESA[e.situacao];
  return (
    <section className="tl-pagina admin-estrutura">
      <CabecalhoTela voltar="/admin/empresas" migalhas={migalhas} titulo={nomeEmpresa(e)}
        subtitulo={<span className="tl-mono">{e.id}{e.nome_fantasia ? ` · ${e.razao_social}` : ""}</span>}
        selos={<span className="linha-titulo selo-cabecalho"><span className={`nx-selo ${classe}`}>{rotulo}</span>{e.simulacao && <Selo tom="suave">simulação</Selo>}</span>}
        acoes={!editando ? <>
          <Link className="nx-botao" to={`/admin/empresas/${e.id}/implantacao`}>Implantação</Link>
          {edita && <button type="button" className="nx-botao nx-botao-principal" onClick={() => setEditando(true)}>Editar</button>}
        </> : undefined} />
      {aviso && <p className="nx-cartao aviso-admin" role="status">{aviso}</p>}
      {editando ? (
        <FormEmpresa inicial={{ ...e, cnpj: e.cnpj ?? "" }} nova={false} enviando={enviando} aoEnviar={(f) => void salvar(f)} aoCancelar={() => setEditando(false)} />
      ) : (
        <div className="estrutura-grade">
          <div className="estrutura-principal">
            <section className="tl-bloco admin-secao" aria-label="Dados da empresa">
              <header><h3>Dados</h3></header>
              <div className="admin-corpo"><FichaEmpresa e={e} /></div>
              {edita && PROXIMAS[e.situacao].length > 0 && (
                <div className="admin-corpo" style={{ borderTop: "1px solid var(--borda)" }}>
                  <div className="form-linha" aria-label="Situação da empresa" role="group">
                    <Campo id="emp-situacao" rotulo="Mudar situação para"
                      ajuda={nova === "suspensa" || nova === "encerrada" ? "Pede o motivo, que fica no histórico." : undefined}>
                      <select id="emp-situacao" value={nova} onChange={(x) => setNova(x.target.value as Empresa["situacao"] | "")}>
                        <option value="">—</option>{PROXIMAS[e.situacao].map((s) => <option key={s} value={s}>{SITUACAO_EMPRESA[s][0]}</option>)}
                      </select>
                    </Campo>
                    <div className="tl-campo"><button type="button" className="nx-botao" disabled={!nova} onClick={aplicar}>Aplicar</button></div>
                  </div>
                </div>
              )}
            </section>
            <Filiais empresa={e.id} edita={edita} aoMudar={carregar} />
            <Municipios empresa={e.id} edita={pode.editarMunicipios(usuario)} aoMudar={carregar} />
            <Setores empresa={e.id} edita={edita} aoMudar={carregar} />
          </div>
          {pode.verEstrutura(usuario) && (
            <aside className="estrutura-lado">
              <section className="tl-bloco admin-secao" aria-label="Histórico do cadastro">
                <header><h3>Histórico do cadastro</h3><span className="tl-subtitulo">nada se apaga</span></header>
                <div className="admin-corpo">
                  <ul className="auditoria tl-linha-tempo">
                    {eventos.map((x, i) => (
                      <li key={i}><b>{EVENTO[x.evento] ?? x.evento}</b>
                        <small>{quando(x.instante)} · por {x.autor}{detalheEvento(x)}</small></li>
                    ))}
                    {!eventos.length && <li className="nx-suave sem-eventos">sem eventos</li>}
                  </ul>
                </div>
              </section>
            </aside>
          )}
        </div>
      )}
      <DialogoMotivo aberto={pedirMotivo} titulo={nova === "encerrada" ? "Encerrar a empresa" : "Suspender a empresa"}
        texto={nova === "suspensa" ? "A operadora fica suspensa até voltar a Ativa. O motivo fica no histórico do cadastro."
          : "Encerrar não tem volta: a operadora sai de operação. O motivo fica no histórico do cadastro."}
        rotuloConfirmar={nova === "encerrada" ? "Encerrar empresa" : "Suspender empresa"} exemplo="ex.: contrato suspenso pela prefeitura"
        aoConfirmar={(m) => mudarSituacao(m)} aoCancelar={() => setPedirMotivo(false)} />
    </section>
  );
}
